import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Tooltip } from "react-tooltip";

import {
  TenantAccount,
  TenantHostnameAttributes,
  TenantHostnames,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient.ts";
import { Authorizations } from "~/api/types";
import { Icon } from "~/assets/icons/Icon.tsx";
import CardRequiredAlert from "~/components/autopto/cardDialog/CardRequiredAlert";
import { isAutoptoCardRequired } from "~/components/autopto/isAutoptoTrial";
import NoData from "~/components/containers/NoData";
import Button from "~/components/ui/button/Button.tsx";
import CopyButton from "~/components/ui/copyButton/CopyButton";
import { useTeam } from "~/contexts/TeamProvider";
import { canCollectionMember } from "~/helpers/authorizations";
import { messageFromError } from "~/helpers/messageFromError.ts";
import { getTenantHostnames } from "~/helpers/tenantHostnames";
import { tryCatch } from "~/helpers/tryCatch.ts";
import { useStagedBoolean } from "~/hooks/useStagedBoolean";

import Input from "../ui/input/Input";
import { DeleteHostnameModal } from "./DeleteHostnameModal";
import { HostnameActionButtons } from "./HostnameActionButtons.tsx";
import styles from "./HostnamesList.module.scss";

type HostnamesListProps = {
  account: TenantAccount;
};

type HostnamesResponse = {
  "tenant/hostnames"?: TenantHostnames;
  tenant_hostnames?: TenantHostnames;
  authorizations?: Authorizations;
};

const COLUMNS_COUNT = 5;
const DNS_TARGET_TOOLTIP_ID = "dns-target-tooltip";

const HostnamesList = ({ account }: HostnamesListProps) => {
  const queryClient = useQueryClient();
  const { team } = useTeam();

  const [newName, setNewName] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const previousHostnamesLengthRef = useRef<number | null>(null);

  const queryKey = ["whiteLabel-hostnames", account.id];
  const [deleteModal, setDeleteModal] = useState<{
    open: boolean;
    id: number | null;
  }>({
    open: false,
    id: null,
  });
  const openDelete = (id: number) => setDeleteModal({ open: true, id });
  const closeDeleteModal = () => setDeleteModal({ open: false, id: null });

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  };

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.tenant.accountHostnamesList({
        headers: {
          "Authorize-Member-Actions": "destroy",
          "Authorize-Collection-Actions": "create",
        },
      });
      const data: HostnamesResponse = response.data;

      return {
        hostnames: getTenantHostnames(data),
        authorizations: data.authorizations,
      };
    },
  });

  const { mutateAsync: createHostname, isPending: hostnameCreatePending } =
    useMutation({
      mutationFn: async (payload: TenantHostnameAttributes) => {
        const result = await tryCatch(
          apiClient.tenant.accountHostnamesCreate(payload),
        );

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });

          throw result.error;
        }

        return result.data;
      },
      onSuccess: () => {
        enqueueSnackbar("Hostname created", { variant: "success" });

        queryClient.invalidateQueries({ queryKey });
      },
    });

  // optimistic
  const { mutate: deleteHostname } = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.tenant.accountHostnamesDelete(id);
    },
    onMutate: async (id: number) => {
      // cancel potential refetches, so it won't override our optimistic state
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData<{
        hostnames: TenantHostnames;
        authorizations?: Authorizations;
      }>(queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          hostnames: old.hostnames.filter((h) => h.id !== id),
        };
      });

      // return snapshot for rollback (in case of error)
      return { previousData };
    },
    onSuccess: () => {
      enqueueSnackbar("Hostname deleted", {
        variant: "success",
      });
    },
    onError: () => {
      enqueueSnackbar("Could not delete hostname", {
        variant: "error",
        persist: true,
      });
      refetch();
    },
  });

  const handleCreate = async () => {
    // don't double-fire while pending
    if (hostnameCreatePending) return;

    const name = newName.trim();

    if (!name) {
      inputRef.current?.focus();

      return;
    }

    await createHostname({ name });
    setNewName("");
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();

      handleCreate();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setNewName("");
      inputRef.current?.blur();
    }
  };

  const hostnames = data?.hostnames ?? [];
  const authorizations = data?.authorizations;

  // scroll to "DNS Target Configuration" if 1st hostname was created
  useEffect(() => {
    const prev = previousHostnamesLengthRef.current;
    const current = data?.hostnames ? data.hostnames.length : null;

    if (prev === null) {
      previousHostnamesLengthRef.current = current;

      return;
    }

    // zero to 1 transition
    if (prev === 0 && current && current > 0) {
      requestAnimationFrame(scrollToBottom);
    }

    previousHostnamesLengthRef.current = current;
  }, [data?.hostnames?.length]);

  const canCreate: boolean = Boolean(
    authorizations?.collection?.includes("create"),
  );

  const isRefetching = isFetching && !isInitialLoading;
  const shouldShowRefreshSpinner = useStagedBoolean(isRefetching, 500, 1000);
  const isLoading = hostnameCreatePending || isInitialLoading || isFetching;
  const isAddBtnDisabled = isLoading || newName.trim().length === 0;
  const shouldShowHostnameCreateSpinner = useStagedBoolean(
    isAddBtnDisabled,
    500,
    1000,
  );

  return (
    <div className={styles.hostnamesListSection}>
      {/* The backend opens a hostname of their own to Autopto agencies
          with a card (the "create" authorization above follows). */}
      {isAutoptoCardRequired(team) && (
        <CardRequiredAlert
          title="Launch your platform on your own domain"
          content="Connect your brand's domain as soon as you add a card."
        />
      )}
      <div className={styles.hostnamesListHeader}>
        {canCreate && (
          <>
            <div className={styles.hostnamesListInlineAddWrapper}>
              <div className={styles.hostnamesListInlineInput}>
                <Input
                  ref={inputRef}
                  placeholder="Enter hostname"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  disabled={isLoading}
                  ownProps={{ noErrors: true }}
                  onKeyDown={onInputKeyDown}
                />
              </div>
              <Button
                className={styles.hostnamesListButton}
                onClick={handleCreate}
                disabled={shouldShowHostnameCreateSpinner}
                withSpinner={hostnameCreatePending}
              >
                {!hostnameCreatePending && <Icon iconName="PlusIcon" />}
                Add
              </Button>
            </div>

            <Button
              disabled={isLoading}
              withSpinner={shouldShowRefreshSpinner}
              className={styles.hostnamesListButton}
              onClick={() => refetch()}
            >
              Refresh Status
            </Button>
          </>
        )}
      </div>

      <table className={styles.hostnamesListTable}>
        <thead>
          <tr>
            <th>Hostname</th>
            <th>
              <div className={styles.dnsTargetTh}>
                DNS Target{" "}
                <span data-tooltip-id={DNS_TARGET_TOOLTIP_ID}>
                  <Icon
                    iconName="QuestionMarkInCircleIcon"
                    width="1rem"
                    height="1rem"
                  />
                </span>
                <Tooltip id={DNS_TARGET_TOOLTIP_ID} place="top">
                  CNAME/ANAME record value
                </Tooltip>
              </div>
            </th>
            <th>DNS</th>
            <th>Certificate</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {isInitialLoading || isRefetching ? (
            <>
              <tr>
                <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                  <Skeleton className={styles.skeletonCell} />
                </td>
              </tr>
              <tr>
                <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                  <Skeleton className={styles.skeletonCell} />
                </td>
              </tr>
              <tr>
                <td className={styles.skeletonTd} colSpan={COLUMNS_COUNT}>
                  <Skeleton className={styles.skeletonCell} />
                </td>
              </tr>
            </>
          ) : hostnames && hostnames.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS_COUNT}>
                <NoData title="No hostnames added yet" />
              </td>
            </tr>
          ) : (
            <>
              {hostnames?.map((hostname, idx) => {
                const canDelete = canCollectionMember(
                  authorizations,
                  idx,
                  "destroy",
                );

                return (
                  <tr key={`${hostname.name}-${idx}`}>
                    <td>
                      <a
                        href={hostname.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.hostnamesListLink}
                        title={hostname.uri}
                      >
                        {hostname.name}
                      </a>
                    </td>
                    <td>
                      {hostname.target}
                      <span className={styles.hostnamesListCopyWrapper}>
                        <CopyButton
                          viewMode="mini"
                          value={hostname.target}
                          className={styles.hostnamesListCopyBtn}
                        />
                      </span>
                    </td>
                    <td>{renderDnsStatusCell(hostname.dns_set)}</td>
                    <td>{renderStatusCell(hostname.acme_status)}</td>
                    <td>
                      {canDelete && (
                        <HostnameActionButtons
                          hostname={hostname}
                          isLoading={isLoading}
                          openDelete={openDelete}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          )}
        </tbody>
      </table>

      <div className={styles.targetConfig}>
        <h2 className={styles.targetConfigTitle}>DNS Target Configuration</h2>
        <p className={styles.targetConfigParagraph}>
          If you are configuring a subdomain, such as sub.example.com or
          www.example.com, please create a CNAME record and paste the DNS Target
          into the Value field.
        </p>
        <p className={styles.targetConfigParagraph}>
          In case of apex domain names, such as example.com, you will need your
          DNS provider to support the ANAME records. The rest is identical to
          the CNAME setup.
        </p>
      </div>
      <div ref={bottomRef}></div>

      <DeleteHostnameModal
        isOpen={deleteModal.open}
        onClose={closeDeleteModal}
        onConfirm={() => {
          if (deleteModal.id == null) return;

          deleteHostname(deleteModal.id);
        }}
      />
    </div>
  );
};

const renderStatusCell = (state: string) => {
  if (state === "ready")
    return <span className={styles.statusReady}>Ready</span>;
  if (state === "completed")
    return <span className={styles.statusReady}>Completed</span>;
  if (state === "updating")
    return <span className={styles.statusPending}>Updating</span>;
  if (state === "pending")
    return <span className={styles.statusPending}>Pending</span>;
  if (state === "error")
    return <span className={styles.statusError}>Error</span>;

  return <span className={styles.statusUnknown}>{state || "Unknown"}</span>;
};

function renderDnsStatusCell(dns_set: boolean) {
  if (dns_set) {
    return <span className={styles.statusReady}>Ready</span>;
  } else {
    return <span className={styles.statusPending}>Pending</span>;
  }
}

export default HostnamesList;
